import { IconButton, TextAndIconButton, TextButton } from "../../../components/button/Button";
import { useTranslation } from "../../../state/i18n";
import { noop } from "lodash-es";
import type { VoidComponent } from "solid-js";
import { createMemo, createSignal, createUniqueId, Show } from "solid-js";
import { CompactTextInput } from "../../../components/inputs/TextInput";
import { APP_BASE_URL, COPY_SUCCESS_MESSAGE_DURATION_MS, MINIMUM_ACTION_LOADING_DELAY } from "../../../constants";
import { makeTimer } from "@solid-primitives/timer";
import ApiService from "../../../services/ApiService";
import type FormId from "../../../state/FormId";
import { DestructiveModal } from "../../../components/modal/Modal";
import { ErrorMessage } from "../../../components/inputs/Input";
import pMinDelay from "p-min-delay";
import GenericFailureModal from "../../GenericFailureModal";
import Popover from "../../../components/popover/Popover";
import Text from "../../../components/text/Text";
import { useModalManager } from "../../../components/modal/ModalManager";

type DirectUrlButtonProps = {
  formId: FormId;
};

const DirectUrlButton: VoidComponent<DirectUrlButtonProps> = (props) => {
  const { t } = useTranslation();
  const getAuthenticationTokensQuery = ApiService.getAuthenticationTokens();
  const authenticationToken = createMemo(
    () => getAuthenticationTokensQuery.data?.find((token) => token.formId.equals(props.formId))?.token,
  );

  return (
    <Popover
      variant="md"
      positioning={{ placement: "bottom-end", gutter: 4 }}
      aria-label={t(($) => $.FORM_DIRECT_URL_POPOVER_TITLE)}
      Title={() => (
        <Text as="h3" truncate text={t(($) => $.FORM_DIRECT_URL_POPOVER_TITLE)} class="text-header-md text-primary" />
      )}
      Trigger={(triggerProps) => (
        <div class="relative">
          <Show when={authenticationToken()}>
            <div
              class="absolute -top-1.5 -right-1.5 rounded-sm"
              role="status"
              aria-label={t(($) => $.FORM_DIRECT_URL_ACTIVE_ARIA_LABEL)}
            >
              <svg
                class="icon-status-success"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="7" cy="7" r="6" fill="currentColor" stroke="white" stroke-width="2" />
              </svg>
            </div>
          </Show>

          <IconButton
            {...triggerProps}
            variant="secondary"
            icon="link"
            aria-label={t(($) => $.FORM_DIRECT_URL_TRIGGER_ARIA_LABEL)}
          />
        </div>
      )}
    >
      <DirectUrlPopoverContent formId={props.formId} authenticationToken={authenticationToken()} />
    </Popover>
  );
};

type DirectUrlPopoverContentProps = {
  formId: FormId;
  authenticationToken: string | undefined;
};
const DirectUrlPopoverContent: VoidComponent<DirectUrlPopoverContentProps> = (props) => {
  const { t } = useTranslation();
  const { open, replace } = useModalManager();
  const [isGeneratingUrl, setIsGeneratingUrl] = createSignal(false);
  const [generateUrlFailed, setGenerateUrlFailed] = createSignal(false);

  const createAuthenticationToken = ApiService.createAuthenticationToken();
  const revokeAuthenticationToken = ApiService.revokeAuthenticationToken();

  return (
    <div class="flex h-full flex-col gap-4">
      <p class="text-base-regular text-secondary">{t(($) => $.FORM_DIRECT_URL_POPOVER_DESCRIPTION)}</p>

      <Show
        when={props.authenticationToken}
        fallback={
          <div class="flex flex-col gap-1 overflow-y-scroll">
            <TextAndIconButton
              variant="secondary"
              text={
                isGeneratingUrl()
                  ? t(($) => $.FORM_DIRECT_URL_POPOVER_ACTIONS_GENERATE_ONGOING)
                  : t(($) => $.FORM_DIRECT_URL_POPOVER_ACTIONS_GENERATE)
              }
              icon="link"
              block
              loading={isGeneratingUrl()}
              onClick={() => {
                setIsGeneratingUrl(true);
                setGenerateUrlFailed(false);

                pMinDelay(
                  createAuthenticationToken.mutateAsync({ formId: props.formId }),
                  MINIMUM_ACTION_LOADING_DELAY,
                  { delayRejection: false },
                )
                  .catch(() => {
                    setGenerateUrlFailed(true);
                  })
                  .finally(() => {
                    setIsGeneratingUrl(false);
                  });
              }}
            />
            <Show when={generateUrlFailed()}>
              <ErrorMessage errorMessage={t(($) => $.FORM_DIRECT_URL_POPOVER_ACTIONS_GENERATE_FAILURE)} />
            </Show>
          </div>
        }
      >
        {(token) => {
          const directUrl = `${APP_BASE_URL}/form/${token()}`;
          const [isCopying, setIsCopying] = createSignal(false);

          return (
            <>
              <div class="flex gap-1">
                <CompactTextInput
                  id={createUniqueId()}
                  onBlur={noop}
                  value={directUrl}
                  disabled
                  ariaLabel={t(($) => $.FORM_DIRECT_URL_POPOVER_INPUT_DIRECT_URL_ARIA_LABEL)}
                />
                <IconButton
                  variant="secondary"
                  icon="externalLink"
                  aria-label={t(($) => $.FORM_DIRECT_URL_POPOVER_ACTIONS_OPEN_ARIA_LABEL)}
                  onClick={() => {
                    window.open(directUrl, "_blank");
                  }}
                />
              </div>

              <div class="flex justify-end gap-1">
                <TextButton
                  variant="secondary"
                  text={t(($) => $.FORM_DIRECT_URL_POPOVER_ACTIONS_REVOKE)}
                  onClick={() => {
                    open({
                      content: () => (
                        <DestructiveModal
                          confirmLabel={t(($) => $.FORM_DIRECT_URL_REVOKE_CONFIRMATION_MODAL_ACTIONS_CONFIRM)}
                          cancelLabel={t(($) => $.FORM_DIRECT_URL_REVOKE_CONFIRMATION_MODAL_ACTIONS_CANCEL)}
                          title={t(($) => $.FORM_DIRECT_URL_REVOKE_CONFIRMATION_MODAL_TITLE)}
                          description={t(($) => $.FORM_DIRECT_URL_REVOKE_CONFIRMATION_MODAL_DESCRIPTION)}
                          onConfirm={() => {
                            revokeAuthenticationToken.mutate(
                              { formId: props.formId },
                              {
                                onError: (err) => {
                                  replace({
                                    content: () => <GenericFailureModal error={err} />,
                                  });
                                },
                              },
                            );
                          }}
                        />
                      ),
                    });
                  }}
                />

                <Show
                  when={!isCopying()}
                  fallback={
                    <TextAndIconButton
                      text={t(($) => $.FORM_DIRECT_URL_POPOVER_ACTIONS_COPY_SUCCESS)}
                      icon="check"
                      disabled
                    />
                  }
                >
                  <TextButton
                    text={t(($) => $.FORM_DIRECT_URL_POPOVER_ACTIONS_COPY)}
                    onClick={() => {
                      navigator.clipboard.writeText(directUrl).then(() => {
                        setIsCopying(true);
                        const dispose = makeTimer(
                          () => {
                            setIsCopying(false);
                            dispose();
                          },
                          COPY_SUCCESS_MESSAGE_DURATION_MS,
                          setTimeout,
                        );
                      });
                    }}
                  />
                </Show>
              </div>
            </>
          );
        }}
      </Show>
    </div>
  );
};

export default DirectUrlButton;
